import React, { FC } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PageApi from 'api/page';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import Modal from 'components/shared/Modal';
import { Alert } from '@kontentino/ui';

import EditProfileTimezoneForm from 'app/modules/posts/components/editProfileTimezoneModal/EditProfileTimezoneForm';
import { useTranslation } from 'react-i18next';

type Props = {
  pageId: number;
  isOpen: boolean;
  onClose(): void;
};

const EditProfileTimezoneModal: FC<Props> = ({ pageId, onClose, isOpen }) => {
  const queryClient = useQueryClient();
  const page = useQuery(
    queryKey.pageDetail(pageId),
    () => PageApi.detail(pageId),
    { enabled: Number.isFinite(pageId) },
  );
  const toast = useToast();
  const { t } = useTranslation();
  const updateTimezone = useMutation(PageApi.update, {
    onSuccess() {
      toast(t('timezoneUpdated'));
      queryClient.invalidateQueries(queryKey.pageTimezone(pageId));

      onClose();
    },
    onError(e: ApiClientError) {
      toast(e.userMessage ?? t('somethingWentWrong'), 'error');
    },
  });

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      onEscKeyDown={onClose}
      isLoading={page.isLoading || updateTimezone.isLoading}
      isContentHeightAutoResizable={false}
    >
      <Modal.Header title={t('editProfileTimeZone')} />
      {page.isError && (
        <Alert variant="danger">{t('somethingWentWrongTryAgain')}</Alert>
      )}
      <EditProfileTimezoneForm
        onClose={onClose}
        initialTimezone={page.data?.timezone}
        onSubmit={(form) => updateTimezone.mutate({ ...form, id: pageId })}
      />
    </Modal>
  );
};

export default EditProfileTimezoneModal;
